import React, { useState, useEffect, useMemo } from 'react'
import { Box, Stack, CircularProgress, Heading, Text } from '@chakra-ui/react'
import moment from 'moment'
import api from '../../../api'
import SimpleBarChart from '../../includes/SimpleBarChart.jsx'
import EmptyBox from '../../includes/EmptyBox.jsx'

function TrialCommLogs ({ trial }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.trials.getCommLogs(trial._id, (data) => {
      setLogs(data.logs)
      setLoading(false)
    })
  }, [trial._id])

  const chartDates = useMemo(() => {
    const oneMonthAgo = moment().subtract(30, 'days')
    const dates = []
    for (let i = 0; i < 31; i++) {
      dates.push(oneMonthAgo.clone().add(i, 'days').toDate())
    }
    return dates
  }, [])

  const datasets = useMemo(() => {
    if (!logs.length) return []
    const emailData = []
    const smsData = []
    const whatsappData = []
    const pushData = []
    chartDates.forEach((date) => {
      const dateLogs = logs.filter(log => moment(log.createdAt).isSame(date, 'day'))
      const emailCount = dateLogs.filter(log => log.type === 'email').length
      const smsCount = dateLogs.filter(log => log.type === 'sms').length
      const whatsappCount = dateLogs.filter(log => log.type === 'whatsapp').length
      const pushCount = dateLogs.filter(log => log.type === 'push').length
      emailData.push(emailCount)
      smsData.push(smsCount)
      whatsappData.push(whatsappCount)
      pushData.push(pushCount)
    })
    return [
      {
        label: 'Email',
        data: emailData,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'SMS',
        data: smsData,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      },
      {
        label: 'WhatsApp',
        data: whatsappData,
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      },
      {
        label: 'Push',
        data: pushData,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  }, [logs, chartDates])

  return (
    <>
      <Heading as='h3' size='md'>{trial.name} communication</Heading>
      <Text>For audit purposes, Trialflare logs emails, text messages, and push notifications sent on behalf of the trial.</Text>

      {loading && <Box mt={10} display='flex' justifyContent='center'><CircularProgress isIndeterminate color='green.300' /></Box>}
      {!loading && !logs.length &&
        <EmptyBox title='No logs have been recorded in the last 30 days' description='Once your trial begins to send out communications, they will be shown here.' />}
      {!loading && logs.length > 0 &&
        <>
          <Stack direction={['column', 'row']} mt={4} spacing={4}>
            {datasets.map((dataset, i) => (
              <Box key={i} borderWidth='1px' borderRadius='lg' p={4}>
                <Heading as='h4' size='sm'>{dataset.label}</Heading>
                <Text fontSize='sm' color='gray.500'>Sent over the last month</Text>
                <Text fontSize='lg'>{dataset.data.reduce((a, b) => a + b, 0)}</Text>
              </Box>
            ))}
          </Stack>

          <Box mt={4} h='300px' borderWidth='1px' borderRadius='lg' p={4}>
            <SimpleBarChart
              labels={chartDates.map(date => moment(date).format('MMM D'))}
              datasets={datasets}
              options={{ maintainAspectRatio: false }}
            />
          </Box>
        </>}
    </>
  )
}

export default TrialCommLogs
